import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Copy,
  FilePlus2,
  FileUp,
  Loader2,
  Search,
  ShieldAlert,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { useRole } from '@/hooks/use-role';
import { ManualLabelImportDialog } from '@/features/manual-labels';

interface AddToManifestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string | null;
  onPdfOpenChange?: (open: boolean) => void;
}

type RecoveryResult = RouterOutputs['customsManifest']['previewBulkManifestRecovery'];

export function AddToManifestDialog({ open, onOpenChange, locationId, onPdfOpenChange }: AddToManifestDialogProps) {
  const { t } = useTranslation('production');
  const { isAdmin, isWarehouseCoordinator, isPacker } = useRole();
  // Written floor sign-off is part of the packing job, so packers and
  // coordinators can use it as well as admins, but only on a floor their own
  // team runs. The server decides that per location (canFloorSignOff) and
  // checks it again on submit.
  const roleCanAttest = isAdmin() || isWarehouseCoordinator() || isPacker();
  const utils = trpc.useUtils();
  const [orderNumbersText, setOrderNumbersText] = useState('');
  const [reviewedText, setReviewedText] = useState('');
  const [result, setResult] = useState<RecoveryResult | null>(null);
  const canAttest = roleCanAttest && result?.canFloorSignOff === true;
  const [reviewing, setReviewing] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [attestationReason, setAttestationReason] = useState('');
  const [attestationConfirmed, setAttestationConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previewRequestRef = useRef(0);
  const locationIdRef = useRef(locationId);
  locationIdRef.current = locationId;

  const availability = trpc.manualLabels.getAvailability.useQuery(
    { locationId: locationId ?? '00000000-0000-0000-0000-000000000000' },
    { enabled: Boolean(locationId) },
  );
  const applyRecovery = trpc.customsManifest.applyBulkManifestRecovery.useMutation();
  const attestRecovery = trpc.customsManifest.adminAttestBulkManifestRecovery.useMutation();
  const writing = applyRecovery.isPending || attestRecovery.isPending;

  const clearReview = useCallback(() => {
    previewRequestRef.current += 1;
    setReviewedText('');
    setResult(null);
    setReviewing(false);
    setAdvancedOpen(false);
    setAttestationReason('');
    setAttestationConfirmed(false);
    setCopied(false);
    setError(null);
  }, []);

  const reset = useCallback(() => {
    clearReview();
    setOrderNumbersText('');
  }, [clearReview]);

  useEffect(() => {
    reset();
  }, [locationId, reset]);

  const close = (next: boolean) => {
    if (!next && writing) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const updateOrderNumbers = (value: string) => {
    setOrderNumbersText(value);
    clearReview();
  };

  const review = async () => {
    const submittedText = orderNumbersText.trim();
    if (!locationId || !submittedText) return;
    const requestId = ++previewRequestRef.current;
    setReviewing(true);
    setResult(null);
    setError(null);
    try {
      const nextResult = await utils.client.customsManifest.previewBulkManifestRecovery.query({
        locationId,
        orderNumbersText: submittedText,
      });
      if (previewRequestRef.current !== requestId) return;
      setReviewedText(submittedText);
      setResult(nextResult);
    } catch (reviewError) {
      if (previewRequestRef.current !== requestId) return;
      setError(getUserMessage(reviewError));
    } finally {
      if (previewRequestRef.current === requestId) setReviewing(false);
    }
  };

  const applyReadyOrders = async () => {
    if (!locationId || !result || reviewedText !== orderNumbersText.trim()) return;
    const submittedLocationId = locationId;
    const reviewVersion = previewRequestRef.current;
    setError(null);
    try {
      const nextResult = await applyRecovery.mutateAsync({ locationId, orderNumbersText: reviewedText });
      void utils.customsManifest.getStatus.invalidate();
      void utils.customsManifest.portlessStatus.invalidate();
      if (locationIdRef.current !== submittedLocationId || previewRequestRef.current !== reviewVersion) return;
      setResult(nextResult);
    } catch (applyError) {
      if (locationIdRef.current !== submittedLocationId || previewRequestRef.current !== reviewVersion) return;
      setError(getUserMessage(applyError));
    }
  };

  const attestMissingLabels = async () => {
    if (!locationId || !result || !canAttest || reviewedText !== orderNumbersText.trim()) return;
    const submittedLocationId = locationId;
    const reviewVersion = previewRequestRef.current;
    setError(null);
    try {
      const nextResult = await attestRecovery.mutateAsync({
        locationId,
        orderNumbersText: reviewedText,
        reason: attestationReason.trim(),
        confirmation: true,
      });
      void utils.customsManifest.getStatus.invalidate();
      void utils.customsManifest.portlessStatus.invalidate();
      if (locationIdRef.current !== submittedLocationId || previewRequestRef.current !== reviewVersion) return;
      setResult(nextResult);
      setAttestationConfirmed(false);
    } catch (attestError) {
      if (locationIdRef.current !== submittedLocationId || previewRequestRef.current !== reviewVersion) return;
      setError(getUserMessage(attestError));
    }
  };

  const copyMissingLabelRequest = async () => {
    if (!result?.missingLabelRequestText) return;
    setError(null);
    try {
      await navigator.clipboard.writeText(result.missingLabelRequestText);
      setCopied(true);
    } catch (copyError) {
      setError(getUserMessage(copyError));
    }
  };

  const readyCount = result?.summary.ready ?? 0;
  const recoveredCount = result?.summary.recovered ?? 0;
  const missingLabelCount = result?.missingLabelOrderNumbers.length ?? 0;
  const reviewOrders = result?.orders.filter((order) =>
    order.actionableReason && order.reasonCode !== 'already_prepared',
  ) ?? [];
  const otherReviewCount = reviewOrders.filter((order) => order.reasonCode !== 'missing_label_evidence').length;
  const attestationReady = attestationReason.trim().length >= 10 && attestationConfirmed;

  return (
    <>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus2 className="h-5 w-5" />
              {t('packer.addToManifest.title')}
            </DialogTitle>
            <DialogDescription>{t('packer.addToManifest.description')}</DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="orders">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="orders">{t('packer.addToManifest.ordersMethod')}</TabsTrigger>
              <TabsTrigger value="pdf">{t('packer.addToManifest.pdfMethod')}</TabsTrigger>
            </TabsList>

            <TabsContent value="orders" className="space-y-4 pt-3">
              <div className="space-y-2">
                <Label htmlFor="manifest-order-numbers">{t('packer.addToManifest.orderLabel')}</Label>
                <Textarea
                  id="manifest-order-numbers"
                  value={orderNumbersText}
                  disabled={reviewing || writing}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  maxLength={50_000}
                  onChange={(event) => updateOrderNumbers(event.target.value)}
                  placeholder={t('packer.addToManifest.orderPlaceholder')}
                  className="min-h-36 font-mono"
                />
                <p className="text-xs text-muted-foreground">{t('packer.addToManifest.pasteHelp')}</p>
              </div>

              <Button
                className="w-full gap-2"
                variant={result ? 'outline' : 'default'}
                disabled={!locationId || !orderNumbersText.trim() || reviewing || writing}
                onClick={() => void review()}
              >
                {reviewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                {reviewing ? t('packer.addToManifest.reviewing') : t('packer.addToManifest.review')}
              </Button>

              {result && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label={t('packer.addToManifest.summaryLabel')}>
                    <SummaryCount label={recoveredCount > 0 ? t('packer.addToManifest.added') : t('packer.addToManifest.ready')} value={recoveredCount || readyCount} />
                    <SummaryCount label={t('packer.addToManifest.needsLabel')} value={missingLabelCount} warning={missingLabelCount > 0} />
                    <SummaryCount label={t('packer.addToManifest.needsReview')} value={otherReviewCount} warning={otherReviewCount > 0} />
                    <SummaryCount label={t('packer.addToManifest.alreadyAdded')} value={result.summary.alreadyPrepared} />
                  </div>

                  {recoveredCount > 0 && (
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertTitle>{t('packer.addToManifest.recoveredTitle', { count: recoveredCount })}</AlertTitle>
                      <AlertDescription>
                        {t('packer.addToManifest.recoveredBody', {
                          customsCount: result.summary.customsPending,
                          inboundCount: result.summary.inboundPending,
                        })}
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="rounded-md border">
                    <div className="border-b px-3 py-2 text-sm font-medium">
                      {t('packer.addToManifest.orderDetails', { count: result.resultCount })}
                    </div>
                    <div className="max-h-64 divide-y overflow-y-auto">
                      {result.orders.map((order, index) => {
                        const statusKey = order.outcome === 'needs_attestation' ? 'needsLabel' : order.outcome;
                        const statusVariant = order.outcome === 'ready' || order.outcome === 'recovered'
                          ? 'success'
                          : order.outcome === 'already_prepared'
                            ? 'muted'
                            : order.reasonCode === 'missing_label_evidence'
                              ? 'warning'
                              : 'error';
                        const evidenceKey = order.evidence.source ?? 'none';
                        return (
                          <div key={`${order.orderNumber}:${order.reasonCode}:${index}`} className="space-y-1 px-3 py-2 text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <span className="break-all font-mono font-medium">
                                {order.reasonCode === 'invalid_order_number' ? order.orderNumber : `#${order.orderNumber}`}
                              </span>
                              <Badge variant={statusVariant}>{t(`packer.addToManifest.status.${statusKey}`)}</Badge>
                            </div>
                            {(order.organizationName || order.classification) && (
                              <p className="text-xs text-muted-foreground">
                                {[order.organizationName, order.classification
                                  ? t(`packer.addToManifest.classification.${order.classification}`)
                                  : null].filter(Boolean).join(' · ')}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {t('packer.addToManifest.evidenceLabel')}: {t(`packer.addToManifest.evidence.${evidenceKey}`)}
                              {' · '}{t('packer.addToManifest.customsLabel')}: {t(`packer.addToManifest.customs.${order.customs.state}`)}
                              {' · '}{t('packer.addToManifest.inboundLabel')}: {t(`packer.addToManifest.inbound.${order.inbound.state}`)}
                            </p>
                            {order.actionableReason && order.reasonCode !== 'already_prepared' && (
                              <p className="text-xs text-foreground">
                                {t(
                                  `packer.addToManifest.reason.${order.reasonCode}${canAttest && readyCount === 0 && order.reasonCode === 'missing_label_evidence' ? '_self' : ''}`,
                                  { defaultValue: order.actionableReason },
                                )}
                              </p>
                            )}
                            {order.warnings.length > 0 && (
                              <p className="text-xs text-amber-700 dark:text-amber-300">
                                {order.warnings.map((warning) => t(`packer.addToManifest.warning.${warning}`)).join(' · ')}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {missingLabelCount > 0 && (
                    <Button variant="outline" className="w-full gap-2" onClick={() => void copyMissingLabelRequest()}>
                      <Copy className="h-4 w-4" />
                      {copied ? t('packer.addToManifest.copiedMissingRequest') : t('packer.addToManifest.copyMissingRequest')}
                    </Button>
                  )}

                  {readyCount > 0 && (
                    <Button className="w-full gap-2" disabled={writing} onClick={() => void applyReadyOrders()}>
                      {applyRecovery.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      {t('packer.addToManifest.addReady', { count: readyCount })}
                    </Button>
                  )}

                  {canAttest && missingLabelCount > 0 && readyCount === 0 && (
                    <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="rounded-md border">
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" className="h-12 w-full justify-between rounded-md px-3">
                          <span className="flex items-center gap-2">
                            <ShieldAlert className="h-4 w-4" />
                            {t('packer.addToManifest.advanced')}
                          </span>
                          <ChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-4 border-t p-3">
                        <Alert variant="warning">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertTitle>{t('packer.addToManifest.attestTitle')}</AlertTitle>
                          <AlertDescription>{t('packer.addToManifest.attestDescription')}</AlertDescription>
                        </Alert>
                        <div className="space-y-2">
                          <Label htmlFor="manifest-attestation-reason">{t('packer.addToManifest.attestReason')}</Label>
                          <Textarea
                            id="manifest-attestation-reason"
                            value={attestationReason}
                            disabled={writing}
                            maxLength={1000}
                            onChange={(event) => setAttestationReason(event.target.value)}
                            placeholder={t('packer.addToManifest.attestReasonPlaceholder')}
                            aria-describedby="manifest-attestation-reason-hint"
                          />
                          <p id="manifest-attestation-reason-hint" className="text-xs text-muted-foreground">
                            {t('packer.addToManifest.attestReasonHint')}
                          </p>
                        </div>
                        <div className="flex min-h-12 items-center gap-3 rounded-md py-2">
                          <Checkbox
                            id="manifest-attestation-confirmation"
                            className="h-6 w-6"
                            checked={attestationConfirmed}
                            disabled={writing}
                            onCheckedChange={(checked) => setAttestationConfirmed(checked === true)}
                          />
                          <Label htmlFor="manifest-attestation-confirmation" className="font-normal leading-5">
                            {t('packer.addToManifest.attestConfirmation', { count: missingLabelCount })}
                          </Label>
                        </div>
                        <Button
                          variant="destructive"
                          className="h-12 w-full gap-2"
                          disabled={writing || !attestationReady}
                          onClick={() => void attestMissingLabels()}
                        >
                          {attestRecovery.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
                          {t('packer.addToManifest.attestAndAdd', { count: missingLabelCount })}
                        </Button>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>{t('packer.addToManifest.failedTitle')}</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="pdf" className="space-y-4 pt-3">
              <Alert>
                <FileUp className="h-4 w-4" />
                <AlertTitle>{t('packer.addToManifest.pdfTitle')}</AlertTitle>
                <AlertDescription>{t('packer.addToManifest.pdfDescription')}</AlertDescription>
              </Alert>
              <Button
                className="w-full gap-2"
                disabled={!locationId || writing || availability.isLoading || !availability.data?.enabled}
                onClick={() => {
                  close(false);
                  setPdfOpen(true);
                  onPdfOpenChange?.(true);
                }}
              >
                {availability.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                {t('packer.addToManifest.pdfChoose')}
              </Button>
              {!availability.isLoading && availability.data && !availability.data.enabled && (
                <p className="text-sm text-muted-foreground">{t('packer.addToManifest.pdfUnavailable')}</p>
              )}
              {availability.isError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>{t('packer.addToManifest.pdfCheckFailed')}</AlertTitle>
                  <AlertDescription>{getUserMessage(availability.error)}</AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <ManualLabelImportDialog
        open={pdfOpen}
        onOpenChange={(next) => {
          setPdfOpen(next);
          onPdfOpenChange?.(next);
        }}
        locationId={locationId}
        purpose="manifest_recovery"
        onApplied={() => {
          void utils.customsManifest.getStatus.invalidate();
          void utils.customsManifest.portlessStatus.invalidate();
        }}
      />
    </>
  );
}

function SummaryCount({ label, value, warning = false }: { label: string; value: number; warning?: boolean }) {
  return (
    <div className={`rounded-md border px-3 py-2 ${warning ? 'border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30' : ''}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
